import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import './CatalogPrograms.css';
import ErrorService from '../core/error-handling/ErrorService';
import { formatDate } from '@/modules/common/helpers/dateHelpers';

// ✅ v0.4.210: Inline getApiUrl() con Railway check para forzar HTTPS
function getApiUrl() {
  const isRailway = window.location.hostname.includes('railway.app');
  if (isRailway) {
    // En Railway, siempre usar HTTPS con el hostname del backend
    return 'https://smarthub-backend-production.up.railway.app';
  }
  // En local, usar variable de entorno o localhost
  const envUrl = import.meta.env.VITE_API_URL;
  return (envUrl ? envUrl.trim() : null) || 'http://localhost:8010';
}

// Función helper para obtener la URL del catálogo dinámicamente
const getCatalogUrl = () => `${getApiUrl()}/api/public/catalog`;

const CatalogPrograms = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    program_type: '',
    mode: '',
    enrolling_only: false
  });
  const [selectedProgram, setSelectedProgram] = useState(null);
  const { t } = useTranslation('public');

  // React Query para lanzamientos (depende de filtros)
  const { data: lanzamientos = [], isLoading: loading } = useQuery({
    queryKey: ['catalog', 'lanzamientos', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.program_type) params.append('program_type', filters.program_type);
      if (filters.mode) params.append('mode', filters.mode);
      if (filters.enrolling_only) params.append('enrolling_only', 'true');
      const response = await fetch(`${getCatalogUrl()}/lanzamientos?${params}`);
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // React Query para filtros disponibles (no depende de filtros seleccionados)
  const { data: availableFilters = { program_types: [], modes: [] } } = useQuery({
    queryKey: ['catalog', 'filters'],
    queryFn: async () => {
      const response = await fetch(`${getCatalogUrl()}/filters`);
      return await response.json();
    },
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });

  // React Query para estadísticas
  const { data: stats = null } = useQuery({
    queryKey: ['catalog', 'stats'],
    queryFn: async () => {
      const response = await fetch(`${getCatalogUrl()}/stats`);
      return await response.json();
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  const loadProgramDetail = async (programId) => {
    try {
      const response = await fetch(`${getCatalogUrl()}/program/${programId}/detail`);
      const data = await response.json();
      setSelectedProgram(data);
    } catch (error) {
      ErrorService.logError(error, 'CatalogPrograms.loadDetalle');
    }
  };


  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  const getProgramTypeLabel = (type) => {
    return t(`catalog.programTypes.${type}`, type);
  };

  const getModeLabel = (mode) => {
    return t(`catalog.modes.${mode}`, mode);
  };

  const getStatusBadge = (status) => {
    const classMap = {
      'enrolling': 'public-catalog-badge-success',
      'planned': 'public-catalog-badge-info',
      'in_progress': 'public-catalog-badge-warning'
    };
    return {
      class: classMap[status] || 'public-catalog-badge-default',
      label: t(`catalog.statusBadges.${status}`, status)
    };
  };

  return (
    <div className="public-catalog-catalog-programs">
      {/* Header con estadísticas */}
      <div className="public-catalog-stats-header">
        <div className="public-catalog-container">
          <div className="public-catalog-header-content">
            <h1>{t('catalog.title')}</h1>
            <button
              className="public-catalog-btn-login"
              onClick={() => navigate('/login')}
              title={t('catalog.loginAccess')}
            >
              🔑 {t('catalog.loginButton')}
            </button>
          </div>
          {stats && (
            <div className="public-catalog-stats-grid">
              <div className="public-catalog-stat-card">
                <span className="public-catalog-stat-value">{stats.total_programs || 0}</span>
                <span className="public-catalog-stat-label">{t('catalog.stats.activePrograms')}</span>
              </div>
              <div className="public-catalog-stat-card">
                <span className="public-catalog-stat-value">{stats.enrolling_now || 0}</span>
                <span className="public-catalog-stat-label">{t('catalog.stats.openEnrollments')}</span>
              </div>
              <div className="public-catalog-stat-card">
                <span className="public-catalog-stat-value">{stats.upcoming_generations || 0}</span>
                <span className="public-catalog-stat-label">{t('catalog.stats.upcomingLaunches')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filtros Horizontales */}
      <div className="public-catalog-filters-section">
        <div className="public-catalog-filters-container">
          <div className="public-catalog-filters-header">
            <span className="public-catalog-icon">🔍</span>
            <h3>{t('catalog.filters.title')}</h3>
          </div>
          <div className="public-catalog-filters-grid">
            <div className="public-catalog-filter-group">
              <label>{t('catalog.filters.programType')}</label>
              <select 
                value={filters.program_type}
                onChange={(e) => setFilters({...filters, program_type: e.target.value})}
              >
                <option value="">{t('catalog.filters.all')}</option>
                {availableFilters.program_types?.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} ({type.count})
                  </option>
                ))}
              </select>
            </div>
            <div className="public-catalog-filter-group">
              <label>{t('catalog.filters.modality')}</label>
              <select
                value={filters.mode}
                onChange={(e) => setFilters({...filters, mode: e.target.value})}
              >
                <option value="">{t('catalog.filters.allFeminine')}</option>
                {availableFilters.modes?.map(mode => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label} ({mode.count})
                  </option>
                ))}
              </select>
            </div>
            <div className="public-catalog-filter-group public-catalog-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={filters.enrolling_only}
                  onChange={(e) => setFilters({...filters, enrolling_only: e.target.checked})}
                />
                {t('catalog.filters.enrollingOnly')}
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Lanzamientos */}
      <div className="public-catalog-main-content">
        <div className="public-catalog-programs-grid">
          {loading ? (
            <div className="public-catalog-loading">{t('catalog.loading')}</div>
          ) : lanzamientos.length === 0 ? (
            <div className="public-catalog-no-results">
              {t('catalog.noResults')}
            </div>
          ) : (
            lanzamientos && Array.isArray(lanzamientos) && lanzamientos.map(item => (
              <div key={item.program.id} className="public-catalog-program-card">
                <div className="public-catalog-program-header">
                  <div className="public-catalog-program-type">
                    <span className="public-catalog-icon">🎓</span>
                    {getProgramTypeLabel(item.program.type)}
                  </div>
                  {item.program.official && (
                    <span className="public-catalog-official-badge">{t('catalog.rvoe')}</span>
                  )}
                </div>
                
                <h3>{item.program.name}</h3>
                <p className="public-catalog-program-code">{t('catalog.code')} {item.program.code}</p>
                
                {item.program.description && (
                  <p className="public-catalog-program-description">{item.program.description}</p>
                )}

                <div className="public-catalog-program-info">
                  <div className="public-catalog-info-item">
                    <span className="public-catalog-icon">⏰</span>
                    <span>{t('catalog.weeksHours', { weeks: item.program.duration_weeks, hours: item.program.total_hours })}</span>
                  </div>
                  <div className="public-catalog-info-item">
                    <span className="public-catalog-icon">📚</span>
                    <span>{getModeLabel(item.program.mode)}</span>
                  </div>
                  {item.program.credits && (
                    <div className="public-catalog-info-item">
                      <span className="public-catalog-icon">🏆</span>
                      <span>{item.program.credits} {t('catalog.credits')}</span>
                    </div>
                  )}
                </div>

                {/* Generaciones Activas */}
                <div className="public-catalog-generations-section">
                  <h4>{t('catalog.availableLaunches')}</h4>
                  {item.active_generations.map(gen => (
                    <div key={gen.id} className="public-catalog-generation-card">
                      <div className="public-catalog-generation-header">
                        <span className="public-catalog-generation-code">{gen.generation_code}</span>
                        <span className={`public-catalog-badge ${getStatusBadge(gen.status).class}`}>
                          {getStatusBadge(gen.status).label}
                        </span>
                      </div>
                      <div className="public-catalog-generation-details">
                        <div className="public-catalog-detail-row">
                          <span className="public-catalog-icon">📅</span>
                          <span>{t('catalog.starts')} {formatDate(gen.start_date)}</span>
                        </div>
                        <div className="public-catalog-detail-row">
                          <span className="public-catalog-icon">💰</span>
                          <span className="public-catalog-price">{formatPrice(gen.price)}</span>
                        </div>
                        {gen.available_spots !== null && (
                          <div className="public-catalog-detail-row">
                            <span className="public-catalog-icon">👥</span>
                            <span>{t('catalog.availableSpots', { count: gen.available_spots })}</span>
                          </div>
                        )}
                        {gen.status === 'enrolling' && (
                          <div className="public-catalog-enrollment-deadline">
                            ⏰ {t('catalog.enrollmentDeadline')} {formatDate(gen.enrollment_end)}
                          </div>
                        )}
                      </div>
                      <button 
                        className="public-catalog-btn public-catalog-btn-primary"
                        onClick={() => loadProgramDetail(item.program.id)}
                      >
                        {t('catalog.viewDetails')}
                      </button>
                    </div>
                  ))}

                  {/* Próxima Generación */}
                  {item.next_generation && (
                    <div className="public-catalog-next-generation">
                      <h5>{t('catalog.nextLaunch')}</h5>
                      <div className="public-catalog-generation-preview">
                        <span>{item.next_generation.generation_code}</span>
                        <span>{t('catalog.starts')} {formatDate(item.next_generation.start_date)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal de Detalle del Programa */}
      {selectedProgram && (
        <div className="public-catalog-modal-overlay" onClick={() => setSelectedProgram(null)}>
          <div className="public-catalog-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="public-catalog-modal-close" onClick={() => setSelectedProgram(null)}>×</button>
            
            <div className="public-catalog-modal-header">
              <h2>{selectedProgram.program.name}</h2>
              <p className="public-catalog-program-type">{getProgramTypeLabel(selectedProgram.program.type)}</p>
            </div>

            <div className="public-catalog-modal-body">
              {selectedProgram.program.description && (
                <div className="public-catalog-section">
                  <h3>{t('catalog.modal.description')}</h3>
                  <p>{selectedProgram.program.description}</p>
                </div>
              )}

              {selectedProgram.program.objectives && (
                <div className="public-catalog-section">
                  <h3>{t('catalog.modal.objectives')}</h3>
                  <ul>
                    {selectedProgram.program.objectives.map((obj, idx) => (
                      <li key={idx}>{obj}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="public-catalog-section">
                <h3>{t('catalog.modal.programInfo')}</h3>
                <div className="public-catalog-info-grid">
                  <div className="public-catalog-info-item">
                    <strong>{t('catalog.modal.duration')}</strong> {t('catalog.weeksHours', { weeks: selectedProgram.program.duration_weeks, hours: selectedProgram.program.total_hours })}
                  </div>
                  <div className="public-catalog-info-item">
                    <strong>{t('catalog.modal.totalHours')}</strong> {selectedProgram.program.total_hours}
                  </div>
                  <div className="public-catalog-info-item">
                    <strong>{t('catalog.modal.modality')}</strong> {getModeLabel(selectedProgram.program.mode)}
                  </div>
                  {selectedProgram.program.credits && (
                    <div className="public-catalog-info-item">
                      <strong>{t('catalog.modal.credits')}</strong> {selectedProgram.program.credits}
                    </div>
                  )}
                  {selectedProgram.program.official && (
                    <div className="public-catalog-info-item">
                      <strong>{t('catalog.modal.rvoe')}</strong> {selectedProgram.program.rvoe_number}
                    </div>
                  )}
                </div>
              </div>

              {selectedProgram.enrolling_now.length > 0 && (
                <div className="public-catalog-section">
                  <h3>{t('catalog.modal.openEnrollments')}</h3>
                  {selectedProgram.enrolling_now.map(gen => (
                    <div key={gen.id} className="public-catalog-enrollment-option">
                      <h4>{gen.code}</h4>
                      <div className="public-catalog-enrollment-details">
                        <p>📅 {t('catalog.modal.startDate')} {gen.start_date}</p>
                        <p>📅 {t('catalog.modal.endDate')} {gen.end_date}</p>
                        <p>⏰ {t('catalog.modal.enrollmentDeadline')} {gen.enrollment_deadline}</p>
                        <p>💰 {t('catalog.modal.price')} {formatPrice(gen.price)}</p>
                        <p>👥 {t('catalog.modal.spotsAvailable')} {gen.spots_available}</p>
                      </div>
                      <button className="public-catalog-btn public-catalog-btn-success">
                        {t('catalog.modal.enrollNow')}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {selectedProgram.upcoming.length > 0 && (
                <div className="public-catalog-section">
                  <h3>{t('catalog.modal.upcomingLaunches')}</h3>
                  {selectedProgram.upcoming.map(gen => (
                    <div key={gen.id} className="public-catalog-upcoming-option">
                      <h4>{gen.code}</h4>
                      <p>📅 {t('catalog.modal.startDate')} {gen.start_date}</p>
                      <p>📝 {t('catalog.modal.enrollmentOpens')} {gen.enrollment_opens}</p>
                      <p>💰 {t('catalog.modal.price')} {formatPrice(gen.price)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogPrograms;
